import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import React, { useEffect, useState } from "react";
import { useGetMatchStudentSemestersQuery, GetMatchStudentSemestersDocument } from "graphqlBase/MatchStudentSemesters/__generated__/getMatchStudentSemesters";
import { useGetStudentSemesterStatusesQuery } from "graphqlBase/StudentSemesterStatus/__generated__/getStudentSemesterStatuses";
import { useAddNewSemesterToStudentMutation } from "graphqlBase/Students/__generated__/addNewSemesterToStudent";
import { useGetCanDeleteSemesterFromStudentQuery } from "graphqlBase/Students/__generated__/getCanDeleteSemesterFromStudent";
import { useDeleteMatchStudentSemestersMutation } from "graphqlBase/MatchStudentSemesters/__generated__/deleteMatchStudentSemesters";
import Props from "./index.d";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import clsx from "clsx";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { studentSemesterStatusIds, MatchStudentSemesterStatusIdStudentSpecific } from "./recoilState";
import { ListSubheader } from "@material-ui/core";

const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles({
      root: {
        width: "100%",
        paddingRight: "20px",
      },
      listRoot: {
        height: "80vh",
      },
      listRootInner: {
        width: "100%",
        maxHeight: "80%",
        overflowY: "auto",
      },
      listItem: {
        paddingLeft: 0,
        paddingRight: 0,
        display: "flex",
        background: theme.colors.grey50,
        marginTop: 0,
      },
      listItemText: {
        flex: "1 1 0",
      },
      header: {
        fontWeight: "bold",
      },
            cancelButton: {
        alignItems: "center",
        appearance: "none",
        backgroundColor: theme.colors.grey300,
        borderRadius: "0px",
        color: theme.colors.tBlack,
        display: "flex",
        flexDirection: "row",
        fontSize: "15px",
        fontWeight: 500,
        justifyContent: "center",
        lineHeight: "26px",
        letterSpacing: "0.46px",
        width: "217px",
        "&:hover": {
          alignItems: "center",
          appearance: "none",
          backgroundColor: theme.colors.grey300,
          borderRadius: "0px",
          color: theme.colors.tBlack,
          display: "flex",
          flexDirection: "row",
          fontSize: "15px",
          fontWeight: 500,
          justifyContent: "center",
          lineHeight: "26px",
          letterSpacing: "0.46px",
          width: "217px",
        },
      },
      confirmButton: {
        alignItems: "center",
        appearance: "none",
        backgroundColor: theme.colors.kolpingOrange,
        borderRadius: "0px",
        color: theme.colors.tWhite,
        display: "flex",
        flexDirection: "row",
        fontSize: "15px",
        fontWeight: 500,
        justifyContent: "center",
        lineHeight: "26px",
        letterSpacing: "0.46px",
        width: "217px",
        "&:hover": {
          alignItems: "center",
          appearance: "none",
          backgroundColor: theme.colors.kolpingOrange,
          borderRadius: "0px",
          color: theme.colors.tWhite,
          display: "flex",
          flexDirection: "row",
          fontSize: "15px",
          fontWeight: 500,
          justifyContent: "center",
          lineHeight: "26px",
          letterSpacing: "0.46px",
          width: "217px",
        },
      },
      headerEnd: {
        // textAlign: "end",
      },
      select: {
        paddingRight: "5px",
      },
      buttonAdd: {
        marginTop: "16px",
        width: "35rem",
        color: theme.colors.tWhite,
      },
      deleteIcon: {
        color: "#d32f2f",
        "&:hover": {
          backgroundColor: "rgba(211, 47, 47, 0.1)",
        },
      },
      deleteColumn: {
        flex: "0 0 50px",
        textAlign: "center",
      },
    }),
  { name: "StudienVerlauf" }
);

const StudienVerlauf: React.FC<Props> = (props) => {
  const {
    data: { state },
  } = props;
  const classes = useStyles();
  const setCurrentStudentSemesterStatusIds = useSetRecoilState(studentSemesterStatusIds);
  const statusValues = useRecoilValue(studentSemesterStatusIds);

  // State für Bestätigungsdialog und Fehler-Toast
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [semesterToDelete, setSemesterToDelete] = useState<{ id: number; name: string } | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const { data } = useGetMatchStudentSemestersQuery({
    variables: {
      filterInput: { studentId: { eq: state } },
    },
    skip: typeof state === "string",
    fetchPolicy: "cache-and-network",
  });

  const { data: statusData } = useGetStudentSemesterStatusesQuery();

  const matchStudentSemesterIds = data?.matchStudentSemesters.map(s => s.id) ?? [];
  const { data: canDeleteData } = useGetCanDeleteSemesterFromStudentQuery({
    variables: { matchStudentSemesterIds },
    skip: matchStudentSemesterIds.length === 0,
  });

  // Mutation pour supprimer un semester
  const [deleteSemester] = useDeleteMatchStudentSemestersMutation({
    refetchQueries: [
      {
        query: GetMatchStudentSemestersDocument,
        variables: { filterInput: { studentId: { eq: state } } },
      },
    ],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setSnackbar({
        open: true,
        message: "Semester erfolgreich gelöscht",
        severity: "success",
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: `Fehler beim Löschen: ${error.message}`,
        severity: "error",
      });
    },
  });

  const canDeleteSemester = (matchStudentSemesterId: number): boolean => {
    const item = canDeleteData?.canDeleteSemesterFromStudent?.find(
      s => s?.matchStudentSemesterId === matchStudentSemesterId
    );
    return item?.canDelete ?? false;
  };

  // Prüfen ob es das letzte Semester ist (nur beim letzten soll das Delete-Icon angezeigt werden)
  const isLastSemester = (matchStudentSemesterId: number, orderedData: typeof memoizedOrderedData): boolean => {
    if (orderedData.length === 0) return false;
    const lastSemester = orderedData[orderedData.length - 1];
    return lastSemester.id === matchStudentSemesterId;
  };

  const handleDeleteSemesterClick = (matchStudentSemesterId: number, semesterName: string) => {
    setSemesterToDelete({ id: matchStudentSemesterId, name: semesterName });
    setDeleteDialogOpen(true);
  };
  const handleConfirmDeleteSemester = async () => {
    if (semesterToDelete) {
      await deleteSemester({
        variables: { ids: [semesterToDelete.id] },
      });
    }
    setDeleteDialogOpen(false);
    setSemesterToDelete(null);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSemesterToDelete(null);
  };

  const [addNewSemester, { loading: addingNewSemester }] = useAddNewSemesterToStudentMutation({
    refetchQueries: [
      {
        query: GetMatchStudentSemestersDocument,
        variables: { filterInput: { studentId: { eq: state } } },
      },
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      const newSemester = data.addNewSemesterToStudent;
      setSnackbar({
        open: true,
        message: `Semester "${newSemester?.zeitraum?.zeitraumNameKuerzel}" erfolgreich hinzugefügt (HS: ${newSemester?.hochschulsemester}, FS: ${newSemester?.fachsemester})`,
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Fehler beim Hinzufügen: ${error.message}`,
        severity: "error",
      });
    },
  });

  const handleAddSemesterClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmAddSemester = async () => {
    setConfirmDialogOpen(false);
    if (typeof state === "number") {
      await addNewSemester({
        variables: { studentId: state },
      });
    }
  };

  const handleCloseDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    if (!data) return;
    if (statusValues[state] != null && Object.keys(statusValues[state]).length === data.matchStudentSemesters.length)
      return;
    const initialStatus = data.matchStudentSemesters.reduce<MatchStudentSemesterStatusIdStudentSpecific>(
      (acc, curr) => {
        if (curr.studentSemesterStatusId) {
          return { ...acc, [curr.id]: curr.studentSemesterStatusId };
        } else {
          return acc;
        }
      },
      {}
    );

    setCurrentStudentSemesterStatusIds({ ...statusValues, [state]: initialStatus });
  }, [data, state]);

  const memoizedOrderedData = React.useMemo(
    () =>
      (data?.matchStudentSemesters ? [...data?.matchStudentSemesters] : []).sort((a, b) => {
        const timeperiodOne = a?.zeitraum?.zeitraumVon ? new Date(a.zeitraum.zeitraumVon).getTime() : 1;
        const timeperiodTwo = b?.zeitraum?.zeitraumVon ? new Date(b.zeitraum.zeitraumVon).getTime() : 1;
        return timeperiodOne - timeperiodTwo;
      }),
    [data, Object.keys(statusValues[state] ?? {}).length]
  );

  const handleChangePre = (key: number) => (event: React.ChangeEvent<{ value: unknown }>) => {
    let value: number | string = event.target.value as string;
    value = parseInt(value);

    const temp = Object.entries(statusValues[state]).reduce((temp, [index, oldValue]) => {
      if (value === 2 && oldValue === 6) {
        if (+index >= key) return { ...temp, [index]: value };
        return temp;
      } else {
        if (+index === key) {
          return { ...temp, [index]: value };
        }
        return temp;
      }
    }, statusValues[state]);
    setCurrentStudentSemesterStatusIds({ ...statusValues, [state]: temp });
    // setCurrentStudentSemesterStatusIds({ ...statusValues, [state]: { ...statusValues[state], [key]: value } });
  };

  let hochschulSemesterCounter = 0;
  let fachSemesterCounter = 0;
  return (
    <div className={classes.root}>
      <div className={classes.listRoot}>
        <div className={classes.listRootInner}>
          <List aria-label="Semester-Status-Tabelle" disablePadding>
            <ListSubheader className={classes.listItem}>
              <ListItemText
                className={clsx(classes.header, classes.headerEnd, classes.listItemText)}
                disableTypography
                primary={"Semester"}
              />
              <ListItemText
                className={clsx(classes.header, classes.headerEnd, classes.listItemText)}
                disableTypography
                primary={"Status"}
              />
              <ListItemText
                className={clsx(classes.header, classes.listItemText)}
                disableTypography
                primary={"Hochschulsemester"}
              />
              <ListItemText
                className={clsx(classes.header, classes.listItemText)}
                disableTypography
                primary={"Fachsemester"}
              />
              <ListItemText
                className={clsx(classes.header, classes.deleteColumn)}
                disableTypography
                primary={""}
              />
            </ListSubheader>
            <Divider />
            {(memoizedOrderedData ?? []).map((entry) => {
              return (
                <div key={entry.id}>
                  <ListItem className={classes.listItem}>
                    <ListItemText
                      className={clsx(classes.headerEnd, classes.listItemText)}
                      disableTypography
                      primary={entry?.zeitraum?.zeitraumNameKuerzel ?? ""}
                    />

                    <ListItemText
                      className={classes.listItemText}
                      disableTypography
                      primary={
                        <div className={classes.select}>
                          {/* {Object.keys(statusValues).length !== 0 && statusValues.constructor === Object && ( */}
                          {statusValues[state] != null && (
                            <Select
                              fullWidth
                              variant="outlined"
                              id="studienverlauf"
                              onChange={handleChangePre(entry.id)}
                              value={statusValues[state][entry.id] ?? entry.studentSemesterStatusId}
                            >
                              {statusData &&
                                statusData.studentSemesterStatuses.map((option) => {
                                  return (
                                    <MenuItem value={option.id} key={option.id}>
                                      {option.studentSemesterStatusName}
                                    </MenuItem>
                                  );
                                })}
                            </Select>
                          )}
                        </div>
                      }
                    />
                    <ListItemText
                      className={classes.listItemText}
                      disableTypography
                      primary={entry.hochschulsemester ?? ""}
                    />
                    <ListItemText
                      className={classes.listItemText}
                      disableTypography
                      primary={entry.fachsemester ?? ""}
                    />
                    <ListItemText
                      className={classes.deleteColumn}
                      disableTypography
                      primary={
                        isLastSemester(entry.id, memoizedOrderedData) && canDeleteSemester(entry.id) && (
                          <IconButton
                            className={classes.deleteIcon}
                            onClick={() => handleDeleteSemesterClick(entry.id, entry?.zeitraum?.zeitraumNameKuerzel ?? "")}
                            size="small"
                            title="Semester löschen"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )
                      }
                    />
                  </ListItem>
                  <Divider />
                </div>
              );
            })}
          </List>
        </div>

        <Button
          variant="contained"
          color="primary"
          className={classes.buttonAdd}
          onClick={handleAddSemesterClick}
          disabled={addingNewSemester || typeof state === "string"}
        >
          {addingNewSemester ? "Wird hinzugefügt..." : "+ Neues Semester hinzufügen"}
        </Button>
      </div>

      <Dialog open={confirmDialogOpen} onClose={handleCloseDialog} PaperProps={{ style: { padding: "17px" } }}>
        <DialogTitle>Neues Semester hinzufügen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie wirklich ein neues Semester für diesen Studenten hinzufügen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button className={classes.cancelButton} onClick={handleCloseDialog} color="default">
            Abbrechen
          </Button>
          <Button className={classes.confirmButton} onClick={handleConfirmAddSemester} color="primary" autoFocus>
            Hinzufügen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} PaperProps={{ style: { padding: "17px" } }}>
        <DialogTitle>Semester löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie das Semester {semesterToDelete?.name ? `"${semesterToDelete.name}"` : ""} wirklich löschen?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button className={classes.cancelButton} onClick={handleCloseDeleteDialog} color="default">
            Abbrechen
          </Button>
          <Button className={classes.confirmButton} onClick={handleConfirmDeleteSemester} color="primary" autoFocus>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default StudienVerlauf;
